import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, ArrowLeft, Check, AlertCircle, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../services/api';
import { trackSignupCompleted } from '../lib/analytics';
import { trackEvent as trackClarityEvent } from '../lib/clarity';

const SUBJECT_OPTIONS = ['Mathematics', 'Science', 'English', 'Hindi', 'Social Studies', 'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Art'];
const CLASS_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export function SignupPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    subjects: [],
    classes: [],
    schoolName: '',
    experience: '',
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleMulti = (k, v) => setForm((p) => ({
    ...p,
    [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v],
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!form.fullName.trim() || !form.email.trim()) { setError('Name and email are required'); return; }
    setIsLoading(true);
    try {
      const res = await api.signupIndividual(form);
      // GA4/Ads conversion — fires only on production host.
      trackSignupCompleted({ method: 'email', plan: res?.plan || 'free' });
      trackClarityEvent('signup_success', { method: 'email', plan: res?.plan || 'free' });
      setResult(res);
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-slate-50 dark:bg-slate-950" data-testid="signup-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white dark:bg-slate-900 rounded-2xl shadow-soft-lg p-8 border border-slate-200 dark:border-slate-800 text-center"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500 flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-heading font-bold mb-1">You're in!</h2>
          <p className="text-sm text-muted-foreground mb-5">Save your User Code — you'll need it to sign in.</p>
          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 text-left mb-5">
            <div className="text-xs text-muted-foreground mb-0.5">User Code</div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-2xl font-mono font-bold tracking-wider" data-testid="signup-user-code">{result.userCode}</div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { navigator.clipboard.writeText(result.userCode); toast.success('Copied'); }}
                data-testid="signup-copy-code"
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="text-xs text-muted-foreground mt-3 mb-0.5">Email</div>
            <div className="text-sm font-semibold">{result.email}</div>
          </div>
          <Button className="w-full" onClick={() => navigate('/login')} data-testid="signup-go-login">
            Go to Sign In
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 bg-slate-50 dark:bg-slate-950 flex items-center justify-center" data-testid="signup-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full"
      >
        <button onClick={() => navigate('/login')} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3" data-testid="back-to-login">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
        </button>

        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-soft-lg p-7 border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-5">
            <img
              src="/brand/logo.png"
              alt="ActanonEdu"
              className="w-12 h-12 rounded-xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
            />
            <div>
              <h2 className="text-xl font-heading font-bold">Create your teacher account</h2>
              <p className="text-xs text-muted-foreground">Independent teachers — no school required</p>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription data-testid="signup-error">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="fullName">Full Name *</Label>
                <Input id="fullName" value={form.fullName} onChange={(e) => update('fullName', e.target.value)} data-testid="signup-name" required />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input id="phone" type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} data-testid="signup-phone" placeholder="Optional" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="signup-email" required />
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => update('password', e.target.value)} data-testid="signup-password" required placeholder="Min 8 chars, letters + digits" />
            </div>

            <div>
              <Label className="mb-1.5 block">Subjects Taught</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="signup-subjects">
                {SUBJECT_OPTIONS.map((s) => {
                  const on = form.subjects.includes(s);
                  return (
                    <button
                      type="button"
                      key={s}
                      onClick={() => toggleMulti('subjects', s)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        on
                          ? 'bg-brand-deep-blue text-white border-brand-deep-blue'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block">Classes Taught</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="signup-classes">
                {CLASS_OPTIONS.map((c) => {
                  const on = form.classes.includes(c);
                  return (
                    <button
                      type="button"
                      key={c}
                      onClick={() => toggleMulti('classes', c)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        on
                          ? 'bg-brand-deep-blue text-white border-brand-deep-blue'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                      }`}
                    >
                      Class {c}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="schoolName">School Name <Badge variant="secondary" className="ml-1 text-[9px]">Optional</Badge></Label>
                <Input id="schoolName" value={form.schoolName} onChange={(e) => update('schoolName', e.target.value)} data-testid="signup-school-name" />
              </div>
              <div>
                <Label htmlFor="experience">Experience <Badge variant="secondary" className="ml-1 text-[9px]">Optional</Badge></Label>
                <Input id="experience" value={form.experience} onChange={(e) => update('experience', e.target.value)} placeholder="e.g. 5 years" data-testid="signup-experience" />
              </div>
            </div>

            <Button type="submit" className="w-full h-11 bg-brand-deep-blue hover:bg-brand-deep-blue/90" disabled={isLoading} data-testid="signup-submit">
              {isLoading ? 'Creating…' : 'Create Account'}
            </Button>

            {/* Legal footer — required for DPDP / Google OAuth verification.
                Lives directly under the CTA so consent is captured at the
                moment the teacher signs up. */}
            <p className="text-center text-[11px] leading-relaxed text-muted-foreground pt-1">
              By creating an account, you agree to our{' '}
              <button
                type="button"
                onClick={() => navigate('/terms')}
                className="text-brand-deep-blue font-medium hover:underline"
                data-testid="signup-terms-link"
              >
                Terms of Service
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() => navigate('/privacy')}
                className="text-brand-deep-blue font-medium hover:underline"
                data-testid="signup-privacy-link"
              >
                Privacy Policy
              </button>
              .
            </p>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
